<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <header class="card-header">
            <strong>Configurações PMAU</strong>
          </header>

          <div class="card-body p-0">
            <b-tabs card>

      <!-- ── Aba definições (admin) ────────────────────────────────────────── -->
      <b-tab v-if="isAdmin" title="Definições de variáveis">
        <div class="d-flex justify-content-end mb-3">
          <b-form-checkbox v-model="mostrarInativos" switch class="mr-3 align-self-center">
            <small>Mostrar inativas</small>
          </b-form-checkbox>
          <button class="btn btn-primary btn-sm" @click="abrirNovo">
            <i class="fa fa-plus mr-1"></i> Nova variável
          </button>
        </div>

        <div v-if="loadingDef" class="loading-container">
          <RotateSquare class="loading-position animated fadeIn" size="60px" />
        </div>
        <b-table
          v-else
          :items="itensDefOrdenados"
          :fields="fields"
          responsive
          striped
          hover
          show-empty
          empty-text="Nenhuma variável cadastrada."
        >
          <template v-slot:cell(code)="data">
            <code class="text-primary">{{ '{' + data.item.code + '}' }}</code>
          </template>
          <template v-slot:cell(variable_type)="data">
            <b-badge :variant="typeBadge(data.item.variable_type)">{{ typeLabel(data.item.variable_type) }}</b-badge>
          </template>
          <template v-slot:cell(default_value)="data">
            <span v-if="data.item.default_value !== null && data.item.default_value !== undefined" class="text-truncate d-block" style="max-width:200px">{{ data.item.default_value || '(vazio)' }}</span>
            <b-badge v-else variant="warning">Obrigatório</b-badge>
          </template>
          <template v-slot:cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">{{ data.item.is_active ? 'Ativa' : 'Inativa' }}</b-badge>
          </template>
          <template v-slot:cell(is_essential)="data">
            <b-badge v-if="data.item.is_essential" variant="dark">
              <i class="fa fa-lock mr-1"></i> Essencial
            </b-badge>
          </template>
          <template v-slot:cell(acoes)="data">
            <b-button
              variant="info"
              size="sm"
              class="mr-1"
              :title="data.item.is_essential ? 'Variável essencial não pode ser editada' : 'Editar'"
              :disabled="data.item.is_essential"
              @click="abrirEdicao(data.item)"
            >
              <i class="fa fa-edit"></i>
            </b-button>
            <b-button
              v-if="data.item.is_active && !data.item.is_essential"
              variant="danger"
              size="sm"
              title="Desativar"
              :disabled="removendo === data.item.id"
              @click="desativar(data.item)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </template>
        </b-table>
      </b-tab>

      <!-- ── Aba valores (todos os usuários) ──────────────────────────────── -->
      <b-tab title="Valores">
        <div v-if="loadingValores" class="text-center py-4">
          <RotateSquare size="50px" class="loading-position animated fadeIn"></RotateSquare>
        </div>
        <div v-else-if="itensValores.length === 0" class="text-muted text-center py-4">
          Nenhuma variável configurável no momento.
        </div>
        <template v-else>
          <div class="alert alert-info py-2 mb-4">
            <small>
              <i class="fa fa-info-circle mr-1"></i>
              Preencha os valores abaixo. Variáveis marcadas como
              <b-badge variant="warning">Obrigatório</b-badge>
              precisam ser preenchidas antes de gerar o conteúdo das etapas que as utilizam.
              Variáveis com valor default serão usadas automaticamente se não preenchidas.
            </small>
          </div>

          <div v-for="item in itensValoresOrdenados" :key="item.id" class="mb-4">
            <label class="mb-1">
              <strong>{{ item.label }}</strong>
              <b-badge v-if="item.is_required && !item.current_value" variant="warning" class="ml-2">Obrigatório</b-badge>
              <b-badge v-else-if="item.is_required && item.current_value" variant="success" class="ml-2">Preenchido</b-badge>
              <b-badge v-else-if="!item.is_required" variant="secondary" class="ml-2">Opcional</b-badge>
              <b-badge v-if="item.is_essential" variant="dark" class="ml-2">
                <i class="fa fa-lock mr-1"></i> Essencial
              </b-badge>
              <code class="ml-2 text-muted" style="font-size:0.8em">{{ '{' + item.code + '}' }}</code>
            </label>
            <div v-if="item.description" class="text-muted small mb-1">{{ item.description }}</div>
            <div v-if="!item.is_required && item.default_value" class="text-muted small mb-1">
              <i class="fa fa-circle-notch mr-1"></i>Default: <em>{{ item.default_value }}</em>
            </div>

            <!-- Tipo imagem -->
            <template v-if="item.variable_type === 'image'">
              <div class="d-flex align-items-start">
                <b-form-input
                  v-model="valores[item.id]"
                  placeholder="URL da imagem (ex: https://...)"
                  class="mr-2"
                  style="max-width: 500px"
                />
                <div>
                  <label :for="'upload-' + item.id" class="btn btn-outline-secondary btn-sm mb-0 cursor-pointer">
                    <i class="fa fa-upload mr-1"></i> Fazer upload
                  </label>
                  <input
                    :id="'upload-' + item.id"
                    type="file"
                    accept="image/*"
                    class="d-none"
                    @change="(e) => uploadImagem(e, item.id)"
                  />
                </div>
              </div>
              <div v-if="valores[item.id]" class="mt-2">
                <img
                  :src="valores[item.id]"
                  style="max-height: 80px; max-width: 200px; border: 1px solid #dee2e6; border-radius: 4px;"
                  @error="(e) => e.target.style.display = 'none'"
                />
              </div>
            </template>

            <!-- Tipo número -->
            <b-form-input
              v-else-if="item.variable_type === 'number'"
              v-model="valores[item.id]"
              type="number"
              :placeholder="item.default_value || '0'"
              style="max-width: 200px"
            />

            <!-- Tipo texto -->
            <b-form-input
              v-else
              v-model="valores[item.id]"
              :placeholder="item.default_value || 'Digite o valor...'"
              style="max-width: 500px"
            />
          </div>

          <hr />
          <button class="btn btn-primary" :disabled="salvando" @click="salvar">
            <span v-if="salvando"><i class="fa fa-spinner fa-spin mr-1"></i> Salvando...</span>
            <span v-else><i class="fa fa-save mr-1"></i> Salvar configurações</span>
          </button>
        </template>
      </b-tab>

            </b-tabs>
          </div><!-- card-body -->
        </div><!-- card -->
      </div>
    </div>

    <!-- Modal criar/editar variável (admin) -->
    <b-modal
      v-if="isAdmin"
      v-model="modalAberto"
      :title="itemEdicao ? 'Editar variável' : 'Nova variável'"
      @ok="salvarDef"
      @hidden="fecharModal"
      ok-title="Salvar"
      cancel-title="Cancelar"
      size="lg"
    >
      <div class="form-group">
        <label><strong>Código</strong> <small class="text-muted">(usado como <code>{codigo}</code> no prompt)</small></label>
        <b-form-input v-model="form.code" placeholder="ex: pmau_telefone" :disabled="!!itemEdicao" />
        <small class="text-muted">Apenas letras minúsculas, números e underscore.</small>
      </div>
      <div class="form-group">
        <label><strong>Rótulo</strong></label>
        <b-form-input v-model="form.label" placeholder="ex: Telefone da Prefeitura" />
      </div>
      <div class="form-group">
        <label><strong>Descrição</strong> <small class="text-muted">(opcional)</small></label>
        <b-form-textarea v-model="form.description" rows="2" placeholder="Instrução de preenchimento..." />
      </div>
      <div class="form-group">
        <label><strong>Tipo</strong></label>
        <b-form-select v-model="form.variable_type" :options="tiposOpcoes" />
      </div>
      <div class="form-group">
        <label>
          <strong>Valor default</strong>
          <small class="text-muted ml-1">— deixe vazio para tornar obrigatório por conta</small>
        </label>
        <b-form-input
          v-model="form.default_value"
          :placeholder="form.variable_type === 'image' ? 'URL da imagem padrão (opcional)' : 'Valor padrão (opcional)'"
        />
        <small class="text-muted">
          Se preenchido: usado quando a conta não configurou o valor.
          Se vazio: a conta é obrigada a preencher antes de gerar.
        </small>
      </div>
      <div v-if="itemEdicao" class="form-group">
        <b-form-checkbox v-model="form.is_active">Variável ativa</b-form-checkbox>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import PmauService from "../../servico/PmauService";

const TYPE_LABELS = { text: "Texto", number: "Número", image: "Imagem" };
const TYPE_BADGE = { text: "secondary", number: "info", image: "primary" };

const EMPTY_FORM = () => ({
  code: "",
  label: "",
  description: "",
  default_value: "",
  variable_type: "text",
  is_active: true,
});

export default {
  name: "PmauConfiguracao",
  components: { RotateSquare },
  data() {
    return {
      // Valores por conta
      loadingValores: false,
      salvando: false,
      itensValores: [],
      valores: {},
      // Definições (admin)
      loadingDef: false,
      itensDef: [],
      mostrarInativos: false,
      modalAberto: false,
      itemEdicao: null,
      removendo: null,
      form: EMPTY_FORM(),
      tiposOpcoes: [
        { value: "text", text: "Texto" },
        { value: "number", text: "Número" },
        { value: "image", text: "Imagem (URL)" },
      ],
      fields: [
        { key: "code", label: "Código", sortable: true },
        { key: "label", label: "Rótulo", sortable: true },
        { key: "variable_type", label: "Tipo", sortable: true },
        { key: "default_value", label: "Default / Obrigatoriedade" },
        { key: "is_active", label: "Status", sortable: true },
        { key: "is_essential", label: "Essencial" },
        { key: "acoes", label: "Ações", thClass: "wd-80-px" },
      ],
    };
  },
  computed: {
    contaId() {
      const auth = this.$store.getters.getAutenticacao;
      return auth && auth.contaSelecionada ? auth.contaSelecionada.id : null;
    },
    isAdmin() {
      return this.$store.getters.isPmauAdmin;
    },
    itensDefOrdenados() {
      return [...this.itensDef].sort((a, b) => (a.is_essential ? 1 : 0) - (b.is_essential ? 1 : 0));
    },
    itensValoresOrdenados() {
      return [...this.itensValores].sort((a, b) => (b.is_essential ? 1 : 0) - (a.is_essential ? 1 : 0));
    },
  },
  watch: {
    mostrarInativos() {
      this.carregarDef();
    },
  },
  beforeMount() {
    this.carregarValores();
    if (this.isAdmin) this.carregarDef();
  },
  methods: {
    // ── Valores por conta ──────────────────────────────────────────────────
    carregarValores() {
      if (!this.contaId) return;
      this.loadingValores = true;
      PmauService.GetVariableValues(this.contaId)
        .then((r) => {
          this.itensValores = r.data;
          const vals = {};
          for (const item of r.data) {
            vals[item.id] = item.current_value || "";
          }
          this.valores = vals;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar configurações PMAU."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loadingValores = false; });
    },
    uploadImagem(event, itemId) {
      const file = event.target.files[0];
      if (!file) return;
      if (!this.contaId) return;
      PmauService.UploadVariableImage(this.contaId, itemId, file)
        .then((response) => {
          this.$set(this.valores, itemId, response.data.value);
          this.$notify({ data: ["Imagem carregada."], type: "success", duration: 2000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao fazer upload da imagem."], type: "warn", duration: 5000 });
        });
      event.target.value = "";
    },
    salvar() {
      if (!this.contaId) return;
      this.salvando = true;
      const payload = this.itensValores.map((item) => ({
        variable_id: item.id,
        value: this.valores[item.id] || "",
      }));
      PmauService.SaveVariableValues(this.contaId, payload)
        .then(() => {
          this.$notify({ data: ["Configurações salvas com sucesso."], type: "success", duration: 3000 });
          this.carregarValores();
        })
        .catch((erro) => {
          const msg = erro?.response?.data?.detail || "Erro ao salvar configurações.";
          this.$notify({ data: [msg], type: "warn", duration: 5000 });
        })
        .finally(() => { this.salvando = false; });
    },
    // ── Definições (admin) ─────────────────────────────────────────────────
    carregarDef() {
      this.loadingDef = true;
      PmauService.ListVariables(this.mostrarInativos)
        .then((r) => { this.itensDef = r.data; })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar variáveis."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loadingDef = false; });
    },
    typeLabel(type) { return TYPE_LABELS[type] || type; },
    typeBadge(type) { return TYPE_BADGE[type] || "secondary"; },
    abrirNovo() {
      this.itemEdicao = null;
      this.form = EMPTY_FORM();
      this.modalAberto = true;
    },
    abrirEdicao(item) {
      this.itemEdicao = item;
      this.form = {
        code: item.code,
        label: item.label,
        description: item.description || "",
        default_value: item.default_value !== null && item.default_value !== undefined ? item.default_value : "",
        variable_type: item.variable_type,
        is_active: item.is_active,
      };
      this.modalAberto = true;
    },
    fecharModal() {
      this.itemEdicao = null;
      this.form = EMPTY_FORM();
    },
    salvarDef(evento) {
      evento.preventDefault();
      if (!this.form.code || !this.form.label) {
        this.$notify({ data: ["Código e Rótulo são obrigatórios."], type: "warn", duration: 4000 });
        return;
      }
      if (!/^[a-z_][a-z0-9_]*$/.test(this.form.code)) {
        this.$notify({ data: ["Código inválido. Use apenas letras minúsculas, números e underscore."], type: "warn", duration: 5000 });
        return;
      }
      const payload = {
        code: this.form.code,
        label: this.form.label,
        description: this.form.description || null,
        default_value: this.form.default_value !== "" ? this.form.default_value : null,
        variable_type: this.form.variable_type,
        is_active: this.form.is_active,
      };
      const request = this.itemEdicao
        ? PmauService.UpdateVariable(this.itemEdicao.id, payload)
        : PmauService.CreateVariable(payload);
      request
        .then(() => {
          this.$notify({ data: ["Variável salva com sucesso."], type: "success", duration: 3000 });
          this.modalAberto = false;
          this.carregarDef();
          this.carregarValores();
        })
        .catch((erro) => {
          const msg = erro?.response?.data?.detail || "Erro ao salvar variável.";
          this.$notify({ data: [msg], type: "warn", duration: 5000 });
        });
    },
    desativar(item) {
      if (!confirm(`Desativar a variável "${item.label}"?`)) return;
      this.removendo = item.id;
      PmauService.DeleteVariable(item.id)
        .then(() => {
          this.$notify({ data: ["Variável desativada."], type: "success", duration: 3000 });
          this.carregarDef();
          this.carregarValores();
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao desativar variável."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.removendo = null; });
    },
  },
};
</script>
